import { i18n } from "@lingui/core";
import { axiosPost } from "@mobsuccess-devops/react-shared/fetch";
import { useToasters } from "@mobsuccess-devops/react-ui/Toaster/use-toasters";
import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import axios from "axios";

export type Send2faAuthResult =
  | { status: "sent"; trust2fa: string | null }
  | { status: "invalid-credentials" }
  | { status: "too-many-attempts"; retryAfterSeconds: number | null };

type Send2faAuthVariables = {
  email: string;
  password: string;
  trust2fa: string | null;
};

export function useSend2faAuth(): UseMutationResult<
  Send2faAuthResult,
  Error,
  Send2faAuthVariables
> {
  const { toast } = useToasters();
  return useMutation({
    mutationKey: ["send2fa-auth"],
    async mutationFn(
      options: Send2faAuthVariables,
    ): Promise<Send2faAuthResult> {
      try {
        const response = await axiosPost<{
          success: boolean;
          trust_2fa?: string;
        }>({
          entity: "auth",
          verb: "send2fa",
          token: null,
          body: {
            email: options.email,
            password: options.password,
            trust_2fa: options.trust2fa,
          },
          doNotRetry: true,
        });
        if (!response.data.success) {
          throw new Error("Something went wrong while sending the 2FA code");
        }
        return { status: "sent", trust2fa: response.data.trust_2fa ?? null };
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          return { status: "invalid-credentials" };
        }
        if (axios.isAxiosError(error) && error.response?.status === 429) {
          return {
            status: "too-many-attempts",
            retryAfterSeconds: readRetryAfter(error.response.data),
          };
        }
        throw error;
      }
    },
    onError() {
      toast.error({
        title: i18n._("src.public.use-send-2fa-auth.error.title"),
      });
    },
  });
}

function readRetryAfter(data: unknown): number | null {
  if (
    typeof data === "object" &&
    data !== null &&
    "retry_after" in data &&
    typeof data.retry_after === "number"
  ) {
    return data.retry_after;
  }
  return null;
}
